import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiErr } from "@/lib/format";
import { EyeIcon, EyeSlashIcon, PlantIcon } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Aviso de sesión vencida o cerrada (lo deja api.js al recibir 401)
  // (se conserva hasta el siguiente inicio de sesión: la redirección puede recargar la página)
  const [aviso] = useState(() => { try { return sessionStorage.getItem("agroport_aviso_sesion") || ""; } catch { return ""; } });

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      try { sessionStorage.removeItem("agroport_aviso_sesion"); } catch { /* sin almacenamiento */ }
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-app">
      {/* Left: hero */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/27901940/pexels-photo-27901940.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Campo agrícola"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-app/60 to-agro-green/30" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/agroport-logo-white.png" alt="AGROPORT" className="h-64 xl:h-72 w-auto" data-testid="login-hero-logo" />
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-tight" data-testid="login-hero-title">
              Impulse su<br />
              <span className="text-agro-green">Producción.</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/agroport-logo-green.png" alt="AGROPORT" className="h-16 w-auto" />
          </div>

          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-agro-green font-medium mb-2">Bienvenido</div>
            <h2 className="font-heading text-3xl font-semibold text-text-primary">Inicia sesión</h2>
          </div>

          {aviso && (
            <div role="status" className="mb-5 text-sm text-agro-yellow bg-agro-yellow/10 border border-agro-yellow/40 rounded-md px-3 py-2" data-testid="login-aviso-sesion">
              {aviso}
            </div>
          )}
          <form onSubmit={submit} className="space-y-5" data-testid="login-form" autoComplete="on">
            <div>
              <label className="text-xs uppercase tracking-wider text-text-secondary font-medium mb-2 block">Correo o usuario</label>
              <input
                data-testid="login-email"
                type="text"
                autoCapitalize="none"
                spellCheck={false}
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-3 text-sm text-text-primary focus:border-agro-green"
                placeholder="usuario@agroport.hn"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-text-secondary font-medium mb-2 block">Contraseña</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-app border border-border rounded-md px-3 py-3 pr-10 text-sm text-text-primary focus:border-agro-green"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                  data-testid="toggle-password"
                >
                  {showPw ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
                </button>
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 transition-all active:scale-[0.98] disabled:opacity-50 shadow-[0_0_24px_rgba(2,191,68,0.25)]"
            >
              {loading ? "Iniciando…" : "Ingresar"}
            </button>

            <div className="text-center pt-2">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-xs text-text-muted hover:text-agro-green transition"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
